import React, { useContext, useState } from 'react';
import { setSessionPriority } from '@/api/sessions';
import { useT, type LabelKey } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { ConsoleContext } from '@/console/ConsoleContext';
import { mapSessionToConversation } from '@/utils/mapSession';
import { CONVERSATION_PRIORITIES, type Conversation, type ConversationPriority } from '@/types';

const LABEL: Readonly<Record<ConversationPriority, LabelKey>> = {
  LOW: 'priority_low',
  MEDIUM: 'priority_medium',
  HIGH: 'priority_high',
  URGENT: 'priority_urgent',
};

/**
 * The conversation's PRIORITY (Low / Medium / High / Urgent) as an editable control, patterned on
 * {@link ConversationCategoryPicker}: a dropdown in the details list, next to the status picker.
 *
 * Saving is `POST /api/sessions/{id}/priority {priority}` (staff only: workspace.handle + ownership, enforced by the
 * server). The control shows what the SERVER answered - the returned session DTO is applied to the row (so
 * `priorityPinned` and every other field arrive as the server has them) - and a refusal leaves the value where it was
 * and says so. `disabled` is the same lock the other editors take (someone else owns the conversation, or the viewer
 * may not act): shown, not editable. A row without a priority reads MEDIUM.
 */
export const ConversationPriorityPicker: React.FC<{
  conv: Conversation;
  disabled?: boolean;
}> = ({ conv, disabled = false }) => {
  const { t } = useT();
  const applyRefreshedRow = useConversationStore((s) => s.applyRefreshedRow);
  const setConversationPriority = useConversationStore((s) => s.setConversationPriority);
  // Optional on purpose (the card also renders where there is no shell): used only to re-read the row.
  const consoleCtx = useContext(ConsoleContext);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current: ConversationPriority = conv.priority ?? 'MEDIUM';

  const save = async (next: ConversationPriority) => {
    if (next === current || saving || disabled) return;
    setSaving(true);
    setError(null);
    const requestedAt = Date.now();
    try {
      const dto = await setSessionPriority(conv.sessionId, next);
      if (dto && dto.sessionId.toLowerCase() === conv.sessionId.toLowerCase()) {
        // The server's row wins (priority, priorityPinned, anything else that moved with it).
        applyRefreshedRow(mapSessionToConversation(dto), requestedAt);
      } else {
        // An answer that is not a session: the value was saved (2xx), so show it and let the server's next read confirm.
        setConversationPriority(conv.sessionId, next, true);
        void consoleCtx?.refreshRow(conv.sessionId);
      }
    } catch {
      setError(t('conv_priority_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="ac-ctx-inline" style={{ gap: 6, flexWrap: 'wrap' }}>
      <select
        className="ac-ctx-select"
        value={current}
        disabled={saving || disabled}
        onChange={(e) => void save(e.target.value as ConversationPriority)}
        aria-label={t('conv_priority')}
        // Stated, not hidden: a manual set pins the priority, so the system stops changing it.
        title={t('conv_priority_pin_hint')}
        data-testid="conv-priority-select"
        data-priority={current}
        style={{
          fontSize: 12,
          fontFamily: 'inherit',
          color: 'var(--text)',
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '2px 6px',
          maxWidth: 150,
          cursor: saving || disabled ? 'default' : 'pointer',
          opacity: saving || disabled ? 0.6 : 1,
        }}
      >
        {CONVERSATION_PRIORITIES.map((p) => (
          <option key={p} value={p}>
            {t(LABEL[p])}
          </option>
        ))}
      </select>

      {error && (
        <span data-testid="conv-priority-error" role="alert" style={{ color: 'var(--danger, #c0392b)', fontSize: 11 }}>
          {error}
        </span>
      )}
    </span>
  );
};
